<template>
  <v-card outlined>
   <v-card-subtitle class="pb-1 text-h6 font-weight-bold text-uppercase ml-2">
  Ürün Görselleri
</v-card-subtitle>


    <v-card-text>
      <input
        ref="fileInput"
        type="file"
        accept="image/*"
        multiple
        @change="handleFileSelect"
        style="display: none"
      />

      <!-- Seçim Butonları -->
      <div class="upload-mode-buttons mb-3">
        <v-btn-toggle v-model="uploadMode" mandatory dense>
          <v-btn value="file" :color="uploadMode === 'file' ? 'primary' : ''">
            <v-icon left small>cloud_upload</v-icon>
            Media Yükle
          </v-btn>
          <v-btn value="link" :color="uploadMode === 'link' ? 'primary' : ''" @click="onYoutubeLinkClick">
            <v-icon left small>link</v-icon>
            Youtube Link Ekle
          </v-btn>
        </v-btn-toggle>
      </div>

      <!-- Uyarı Modalı -->
      <v-dialog v-model="showWarningDialog" max-width="400">
        <v-card>
          <v-card-title class="headline warning white--text">
            <v-icon left color="white">warning</v-icon>
            Uyarı
          </v-card-title>
          <v-card-text class="pt-4 text-center">
            <v-icon x-large color="warning" class="mb-3">error_outline</v-icon>
            <p class="text-h6">{{ warningMessage }}</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="warning" text @click="showWarningDialog = false">
              Tamam
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Media Yükle Alanı -->
      <div 
        v-if="uploadMode === 'file'"
        class="upload-area" 
        @click="triggerFileInput"
        :class="{ 'uploading': uploading }"
      >
        <div v-if="!uploading" class="text-center">
          <v-icon large color="primary">cloud_upload</v-icon>
          <p class="mt-2 mb-0">Görsel Yüklemek İçin Tıklayın</p>
        </div>
        
        <div v-else class="text-center">
          <v-progress-circular
            :value="uploadProgress"
            :size="50"
            color="primary"
          >
            {{ Math.round(uploadProgress) }}%
          </v-progress-circular>
          <p class="mt-2 mb-0">Yükleniyor...</p>
        </div>
      </div>

      <!-- Link Ekle Alanı -->
      <div v-if="uploadMode === 'link'" class="link-area">
        <v-row align="center" no-gutters>
          <v-col cols="9" sm="10">
            <v-text-field
              v-model="manualImageUrl"
              label="Görsel Linki"
              placeholder="https://..."
              outlined
              dense
              hide-details
              prepend-inner-icon="link"
              @keyup.enter="addManualLink"
            ></v-text-field>
          </v-col>
          <v-col cols="3" sm="2" class="pl-2">
            <v-btn
              color="primary"
              :disabled="!isValidUrl"
              @click="addManualLink"
              block
            >
              <v-icon small>add</v-icon>
              Ekle
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <div v-if="images.length > 0" class="mt-4">
        <p class="caption grey--text mb-2">
          <v-icon small color="grey">info</v-icon>
          Görselleri sürükleyerek sıralayabilirsiniz. İlk görsel ön kapak olacaktır.
        </p>
        <div class="image-grid">
          <v-card 
            v-for="(image, index) in images" 
            :key="image.fileKey"
            class="image-card"
            outlined
            draggable="true"
            @dragstart="onDragStart($event, index)"
            @dragover.prevent
            @drop="onDrop($event, index)"
            @dragenter="onDragEnter($event, index)"
            @dragleave="onDragLeave($event)"
          >
            <div class="image-order-badge">
              {{ index + 1 }}
            </div>
            
            <div class="drag-handle">
              <v-icon small color="white">drag_indicator</v-icon>
            </div>
            
            <v-img
              v-if="!image.hasError"
              :src="image.previewUrl"
              height="150"
              contain
              @error="onImageError(index)"
            >
              <template v-slot:placeholder>
                <div class="image-placeholder">
                  <v-icon large color="grey">image</v-icon>
                  <p class="caption grey--text mb-0">Yükleniyor...</p>
                </div>
              </template>
              
              <v-btn
                icon
                small
                color="error"
                class="delete-btn"
                @click.stop="removeImage(index)"
              >
                <v-icon small color="red">delete</v-icon>
              </v-btn>
              
              <!-- Link ikonu - görsel link ise göster -->
              <div v-if="image.isExternalLink" class="link-badge">
                <v-icon x-small color="white">link</v-icon>
              </div>
            </v-img>
            
            <!-- Görsel yüklenemezse fallback -->
            <div v-if="image.hasError" class="image-fallback" style="height: 150px; position: relative;">
              <v-btn
                icon
                small
                color="error"
                class="delete-btn"
                @click.stop="removeImage(index)"
              >
                <v-icon small color="red">delete</v-icon>
              </v-btn>
              <v-icon large color="orange">link</v-icon>
              <p class="caption orange--text mb-0 mt-1">Harici Link</p>
            </div>
            
            <v-card-text class="pa-2">
              <p class="caption mb-0 text-truncate" :title="image.fileName">{{ image.fileName }}</p>
            </v-card-text>
          </v-card>
        </div>
      </div>

      <p v-else class="text-center grey--text caption mt-3 mb-0">
        Henüz görsel eklenmedi
      </p>
    </v-card-text>
  </v-card>
</template>

<script>
import Api from '@/api/baseApi';
import axios from 'axios';

export default {
  name: 'ProductImageUpload',
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      images: [],
      uploading: false,
      uploadProgress: 0,
      draggedIndex: null,
      manualImageUrl: '',
      uploadMode: 'file',
      showWarningDialog: false,
      warningMessage: ''
    };
  },
  computed: {
    isValidUrl() {
      if (!this.manualImageUrl) return false;
      const url = this.manualImageUrl.trim();
      return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('//');
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0 && this.images.length === 0) {
          this.loadExistingImages(newVal);
        }
      }
    }
  },
  methods: {
    isYoutubeLink(url) {
      if (!url) return false;
      const lowerUrl = url.toLowerCase();
      return lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be');
    },

    hasNonVideoMedia() {
      return this.images.some(img => !this.isYoutubeLink(img.fileKey));
    },

    onYoutubeLinkClick() {
      if (!this.hasNonVideoMedia()) {
        this.warningMessage = 'Media yüklemeden YouTube linki eklenemez.';
        this.showWarningDialog = true;
        // Revert to file mode
        this.$nextTick(() => {
          this.uploadMode = 'file';
        });
      }
    },
    
    triggerFileInput() {
      if (!this.uploading) {
        this.$refs.fileInput.click();
      }
    },

    async handleFileSelect(event) {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      this.uploading = true;
      this.uploadProgress = 0;

      try {
        for (let i = 0; i < files.length; i++) {
          await this.uploadFile(files[i]);
          this.uploadProgress = ((i + 1) / files.length) * 100;
        }

        this.$emit('input', this.images.map(img => img.fileKey));
        
      } catch (error) {
        console.error('Upload error:', error);
        const errorMessage = error.response && error.response.data && error.response.data.error 
          ? error.response.data.error 
          : error.message;
        alert('Yükleme hatası: ' + errorMessage);
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
        this.$refs.fileInput.value = '';
      }
    },

    async uploadFile(file) {
      const presignedResponse = await Api.post('/api/upload/presigned-url', {
        fileName: file.name,
        contentType: file.type
      });

      const { uploadUrl, fileKey } = presignedResponse.data;

      // 2. Doğrudan R2'ye yükle
      await axios.put(uploadUrl, file, {
        headers: {
          'Content-Type': file.type
        }
      });

      // 3. Preview URL oluştur
      const previewUrl = URL.createObjectURL(file);

      // 4. Images array'e ekle
      this.images.push({
        fileKey,
        previewUrl,
        fileName: file.name,
        isExternalLink: false,
        hasError: false
      });
    },

    async loadExistingImages(fileKeys) {
      this.images = [];
      
      for (const fileKey of fileKeys) {
        try {
          let previewUrl;
          let isExternalLink = false;
          
          if (fileKey.startsWith('http://') || fileKey.startsWith('https://') || fileKey.startsWith('//')) {
            previewUrl = fileKey;
            isExternalLink = true;
          } else {
            const response = await Api.post('/api/upload/presigned-download-url', {
              fileKey
            });
            previewUrl = response.data.downloadUrl;
          }
          
          this.images.push({
            fileKey,
            previewUrl: previewUrl,
            fileName: fileKey.split('/').pop(),
            isExternalLink: isExternalLink,
            hasError: false
          });
        } catch (error) {
          console.error('Görsel yükleme hatası:', error);
        }
      }
    },

    removeImage(index) {
      const imageToDelete = this.images[index];
      const isCurrentImageYoutube = this.isYoutubeLink(imageToDelete.fileKey);
      
      if (!isCurrentImageYoutube) {
        const nonVideoImages = this.images.filter(img => !this.isYoutubeLink(img.fileKey));
        const youtubeImages = this.images.filter(img => this.isYoutubeLink(img.fileKey));
        
        if (nonVideoImages.length === 1 && youtubeImages.length > 0) {
          this.warningMessage = 'YouTube video linki silinmeden tek kalan media silinemez.';
          this.showWarningDialog = true;
          return;
        }
      }
      
      if (confirm('Bu görseli silmek istediğinize emin misiniz?')) {
        this.images.splice(index, 1);
        this.$emit('input', this.images.map(img => img.fileKey));
      }
    },

    addManualLink() {
      if (!this.isValidUrl) return;
      
      const url = this.manualImageUrl.trim();
      
      if (this.isYoutubeLink(url) && !this.hasNonVideoMedia()) {
        this.warningMessage = 'Media yüklemeden YouTube linki eklenemez.';
        this.showWarningDialog = true;
        return;
      }
      
      if (this.isYoutubeLink(url) && this.images.length === 0) {
        this.warningMessage = 'Video linki ilk sıraya konulamaz.';
        this.showWarningDialog = true;
        return;
      }
      
      const fileName = url.split('/').pop() || 'external-image';
      
      this.images.push({
        fileKey: url,
        previewUrl: url,
        fileName: fileName,
        isExternalLink: true,
        hasError: false
      });
      
      this.$emit('input', this.images.map(img => img.fileKey));
      this.manualImageUrl = '';
    },

    onImageError(index) {
      if (this.images[index]) {
        this.$set(this.images[index], 'hasError', true);
      }
    },

    onDragStart(event, index) {
      this.draggedIndex = index;
      event.dataTransfer.effectAllowed = 'move';
      event.target.style.opacity = '0.5';
    },

    onDragEnter(event, index) {
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        const card = event.target.closest('.image-card');
        if (card) {
          card.classList.add('drag-over');
        }
      }
    },

    onDragLeave(event) {
      const card = event.target.closest('.image-card');
      if (card) {
        card.classList.remove('drag-over');
      }
    },

    onDrop(event, dropIndex) {
      event.preventDefault();
      const card = event.target.closest('.image-card');
      if (card) {
        card.classList.remove('drag-over');
      }
      
      if (this.draggedIndex !== null && this.draggedIndex !== dropIndex) {
        const draggedItem = this.images[this.draggedIndex];
        
        if (dropIndex === 0 && this.isYoutubeLink(draggedItem.fileKey)) {
          this.warningMessage = 'Video linki ilk sıraya konulamaz.';
          this.showWarningDialog = true;
          this.draggedIndex = null;
          const cards = document.querySelectorAll('.image-card');
          cards.forEach(function(c) {
            c.style.opacity = '1';
          });
          return;
        }
        
        // Array'i yeniden sırala
        this.images.splice(this.draggedIndex, 1);
        this.images.splice(dropIndex, 0, draggedItem);
        
        // Parent component'e bildir
        this.$emit('input', this.images.map(img => img.fileKey));
      }
      
      this.draggedIndex = null;
      
      // Opacity'yi resetle
      const cards = document.querySelectorAll('.image-card');
      cards.forEach(function(c) {
        c.style.opacity = '1';
      });
    }
  }
};
</script>

<style scoped>
.upload-mode-buttons {
  display: flex;
  justify-content: center;
}

.upload-area {
  border: 2px dashed #1976d2;
  border-radius: 8px;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #f5f5f5;
}

.upload-area:hover:not(.uploading) {
  border-color: #1565c0;
  background-color: #e3f2fd;
}

.upload-area.uploading {
  cursor: not-allowed;
  opacity: 0.7;
}

.link-area {
  border: 2px solid #1976d2;
  border-radius: 8px;
  padding: 20px;
  background-color: #f5f5f5;
}

.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  background-color: #f5f5f5;
}

.image-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-radius: 4px;
}

.link-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(25, 118, 210, 0.9);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.image-card {
  position: relative;
  cursor: move;
  transition: all 0.3s;
}

.image-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.image-card.drag-over {
  border: 2px solid #1976d2;
  background-color: #e3f2fd;
}

.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(255, 255, 255, 0.9) !important;
  z-index: 2;
}

.delete-btn:hover {
  background-color: rgba(255, 255, 255, 1) !important;
}

.image-order-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background-color: #1976d2;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.drag-handle {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background-color: rgba(25, 118, 210, 0.9);
  border-radius: 4px;
  padding: 4px;
  cursor: move;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drag-handle:hover {
  background-color: rgba(25, 118, 210, 1);
}
</style>

